import { inject, injectable } from "inversify";
import type { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { webScriptBackend } from "./webScriptBackend";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage, STORAGE } from "@whatslly/commons/storage.constants";
import { IMUserStore } from "@whatslly/instant-messaging/IMUserStore";
import { KeywordInsightsSettings } from "@whatslly/types/keyword-insights/KeywordInsightsSettings";
import { KeywordInsightsCampaignType, KeywordsInsightsDirection } from "@whatslly/types/keyword-insights/KeywordInsightsCampaign";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { backgroundClient } from "@whatslly/commons/background/client";
import { ReportKeywordsBody } from "@whatslly/types/integrations/KeywordInsights";
import { ContactService } from "@whatslly/instant-messaging/contactService";
import { testCampaignConditions, getCampaignMatchedKeywords } from "@whatslly/commons/keywordInsightsCommonUtils";
import type { EmittedMessage, IMessageEmitter } from "@whatslly/types/instant-messaging/messagesEmitter";
import { AllChats, InjectMessageEmitter } from "@whatslly/types/instant-messaging/messagesEmitter";
import { MessageSyncService } from "./messageSyncService";
import ms from "ms";
import { contentBackend } from "@whatslly/commons/contentBackend";
import { ContentCommands } from "@whatslly/commons/contentCommands";
import { reaction } from "mobx";

export const InjectKeywordInsights = Symbol("InjectKeywordInsights");
export interface IKeywordInsights {
  init(selectors: WhatsappSelectorsType): Promise<void>;
}
@injectable()
export class MessageKeywordInsightsService implements IKeywordInsights {
  protected selectors?: WhatsappSelectorsType;
  protected logger: FrontLogger;
  #keywordInsightsSettings: KeywordInsightsSettings | null = null;
  private abortController: AbortController | undefined;
  constructor(
    @inject(FrontLogger) frontLogger: FrontLogger,
    @inject(InjectStorage) protected readonly storage: IStorage,
    @inject(IMUserStore) protected readonly imUserStore: IMUserStore,
    @inject(ContactService) private readonly contactService: ContactService,
    @inject(InjectMessageEmitter) private readonly MessageEmitter: IMessageEmitter<any, any>
  ) {
    this.logger = frontLogger.getLogger("message-keyword-insights-service");
  }

  reset() {
    this.#keywordInsightsSettings = null;
  }

  public async scanTextMessage(message: EmittedMessage) {
    const msg = message.text;
    if (!this.hasKeywordPolicies || !msg?.length) return;

    const campaigns = this.#keywordInsightsSettings?.campaigns ?? [];
    const matchedCampaigns = campaigns.filter((campaign) =>
      testCampaignConditions(campaign, {
        content: msg,
        direction: message.isSentByMe ? KeywordsInsightsDirection.OUTBOUND : KeywordsInsightsDirection.INBOUND,
        user: this.imUserStore.user,
      })
    );
    if (matchedCampaigns.length === 0) return;

    await Promise.all(
      matchedCampaigns.map((campaign) =>
        this.reportCampaignMatch(campaign, msg, {
          sender: { name: message.senderName, phone: message.senderPhone, isGroup: message.isGroupMsg },
          receiver: { name: message.receiverName, phone: message.receiverPhone, isGroup: message.isGroupMsg },
          msgId: message.serializedMessageId,
          channelType: message.channelType ?? "whatsAppWeb",
          isSentByMe: message.isSentByMe,
          isGroup: message.isGroupMsg,
        })
      )
    );
  }

  private async reportCampaignMatch(
    campaign: KeywordInsightsCampaignType,
    content: string,
    metadata: {
      sender: { name: string; phone: string; isGroup: boolean } | undefined;
      receiver: { name: string; phone: string; isGroup: boolean } | undefined;
      msgId: string;
      channelType: string;
      isSentByMe: boolean;
      isGroup: boolean;
    }
  ) {
    if (!this.contactService.isWhitelisted) return;
    const payload = {
      message: {
        sender: metadata?.sender?.name ?? "-",
        senderPhone: metadata?.sender?.phone ?? "-",
        receiver: metadata?.receiver?.name ?? "",
        recipientPhone: metadata?.receiver?.phone ?? "",
        content: this.shouldReportMessageContent ? content : undefined,
        isGroup: metadata?.isGroup ?? false,
        msgId: metadata?.msgId,
        channelType: metadata?.channelType,
        direction: metadata?.isSentByMe ? KeywordsInsightsDirection.OUTBOUND : KeywordsInsightsDirection.INBOUND,
      },
      matchedKeywords: getCampaignMatchedKeywords(campaign, content),
    } as ReportKeywordsBody;

    await backgroundClient.send(BgCommands.HTTP_REQUEST, {
      urlSuffix: `/integrations/keyword-insights/campaigns/${campaign._id}/report`,
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  private async scrapeMessages() {
    if (!this.imUserStore.user) return;
    this.abortController?.abort();
    this.abortController = new AbortController();
    const settings = MessageSyncService.getListenerSettings(this.imUserStore.user!);
    const syncronizeLastDays = ms(`${this.imUserStore.user?.syncronizeLastDays || 1} days`);
    const maxMessageAge = syncronizeLastDays;
    const scraper = new this.MessageEmitter(
      AllChats,
      {
        mediaMode: "binary",
        ...settings,
        ignoreOldMessages: true,
        listenToNewMessages: true,
        scrollToEarliestMessage: false,
        maxMessageAge: maxMessageAge ? Math.min(maxMessageAge, settings.maxMessageAge) : settings.maxMessageAge,
        abortSignal: this.abortController.signal,
      },
      this.logger
    );
    for await (const message of scraper) {
      if (!message.text) continue;
      this.scanTextMessage(message);
    }
  }

  private get shouldReportMessageContent() {
    return this.#keywordInsightsSettings?.reportMessageContent ?? false;
  }

  private async getKeywordPolicies() {
    this.#keywordInsightsSettings = await this.storage.get(STORAGE.KEYWORD_INSIGHTS_SETTINGS);
  }

  get hasKeywordPolicies() {
    if (!this.#keywordInsightsSettings) return false;
    return this.#keywordInsightsSettings.campaigns.length > 0;
  }

  get isKeywordInsightsEnabled(): boolean {
    return Boolean(this.imUserStore.user?.allow_keyword_insights);
  }

  async init(selectors: WhatsappSelectorsType) {
    this.selectors = selectors;
    this.MessageEmitter.inject(selectors);
    webScriptBackend
      .on(WebScriptCommands.LOGOUT, () => this.reset())
      .on([WebScriptCommands.USER_REFRESHED, WebScriptCommands.AUTH], async () => {
        if (!this.isKeywordInsightsEnabled) {
          this.abortController?.abort();
          return;
        }
        await this.getKeywordPolicies();
        this.scrapeMessages();
      })
      .on(WebScriptCommands.KEYWORD_POLICIES_CHANGED, async () => {
        if (!this.isKeywordInsightsEnabled) {
          this.abortController?.abort();
          return;
        }
        await this.getKeywordPolicies();
        this.scrapeMessages();
      });
    contentBackend.on(ContentCommands.CONTENT_READY, async () => {
      this.MessageEmitter.inject(selectors);
    });

    reaction(
      () => this.isKeywordInsightsEnabled,
      async (value) => {
        if (value) {
          await this.getKeywordPolicies();
          this.scrapeMessages();
          return;
        }
        this.abortController?.abort();
      }
    );
  }
}
